"use client";
import React, { useCallback, useEffect, useState } from "react";
import { algoliaSearchRefine, constructAlgoliaIndexName } from "@/apiUtils/algoliaUtils";
import {
  AlgoliaProductItem,
  DevAeWebAEDProducts,
  SearchQuerySuggestions,
  SearchResultItem,
  ValueCount,
} from "@/apiUtils/algoliaUtils/types";
import { hasArrayElements } from "@/apiUtils/commonUtils";
import useCRouter from "@/hooks/useCRouter";
import ProductCarousel from "@/components/productCarousel/ProductCarousel";
import Button from "@/components/ui/button/Button";
import { analyticEvents, handlePostAnalytics } from "@/apiUtils/analyticsUtils";
import { analyticEventSources, analyticEventTypes } from "@/apiUtils/constants";

export interface IProps {
  open: boolean;
  searchTerm: string;
  locale: string;
  lang: string;
  translation: Record<string, string>;
  onClose: () => void;
  enableVto: boolean;
}

const initialQuerySuggestions: SearchQuerySuggestions = {
  "brand.name.en": [],
  "brand.name.ar": [],
  "categories.en.lvl0": [],
  "categories.ar.lvl0": [],
  suggestion: [],
};

const SuggestionList = ({
  open,
  searchTerm,
  locale,
  lang,
  translation,
  onClose,
  enableVto,
}: IProps) => {
  const indexName = constructAlgoliaIndexName(locale);
  const router = useCRouter();

  const [querySuggestions, setQuerySuggestion] =
    useState<SearchQuerySuggestions>(initialQuerySuggestions);
  const [searchProducts, setSearchProducts] = useState<AlgoliaProductItem[]>([]);

  const transformData = useCallback(
    (data: SearchResultItem[]) => {
      const uniqueBrands = new Set<string>();
      const uniqueCategories = new Set<string>();
      const searchSuggestions = new Set<string>();

      if (hasArrayElements(data)) {
        data.forEach((item) => {
          const productDetails = item[indexName] as DevAeWebAEDProducts | undefined;
          if (!productDetails) return;

          const { exact_matches: exactMatches } = productDetails.facets;
          searchSuggestions.add(item.query);

          const brandFacet = exactMatches[`brand.name.${lang}` as keyof typeof exactMatches];
          const categoryFacet =
            exactMatches[`categories.${lang}.lvl0` as keyof typeof exactMatches];

          if (hasArrayElements(brandFacet)) {
            brandFacet.forEach((facet: ValueCount) => uniqueBrands.add(facet.value));
          }

          if (hasArrayElements(categoryFacet)) {
            categoryFacet.forEach((facet: ValueCount) => uniqueCategories.add(facet.value));
          }
        });
      }

      const toArray = (set: Set<string>) => Array.from(set).map((value) => ({ value }));

      return {
        [`brand.name.${lang}`]: toArray(uniqueBrands),
        [`categories.${lang}.lvl0`]: toArray(uniqueCategories),
        suggestion: toArray(searchSuggestions),
      };
    },
    [indexName, lang],
  );

  const getQuerySuggestions = useCallback(() => {
    algoliaSearchRefine(indexName, lang, searchTerm).then((response) => {
      if (response) {
        const newProducts = response?.products?.hits || [];
        const suggestions = transformData(response.suggestions?.hits || []);
        setSearchProducts(newProducts);
        setQuerySuggestion(suggestions);
      }
    });
  }, [indexName, lang, searchTerm, transformData]);

  const handleClickSuggestion = (pathname: string, source?: string) => {
    router.push(pathname);
    onClose();
    if (source === "carousel") {
      handlePostAnalytics({
        eventType: analyticEventTypes.DETAILS,
        data: {
          target: analyticEvents.VIEW_ITEM_LIST,
          products: searchProducts,
        },
      });
    }

  };

  useEffect(() => {
    if (open) {
      getQuerySuggestions();
    }
  }, [open, getQuerySuggestions]);

  return (
    <>
      <div className="lg:grid lg:grid-cols-4 lg:px-16 lg:pb-8">
        {/* Suggested Products */}
        {searchProducts?.length > 0 && (
          <div className="mt-4">
            <h4 className="text-lg font-semibold">
              {translation?.common_header_search_products_title || ""}
            </h4>
            <ul>
              {searchProducts?.map((item, index) => (
                <li key={index} className="mt-2 text-sm">
                  <button
                    className="text-start"
                    onClick={() => handleClickSuggestion(`${item?.defaultVariant?.url || ""}`)}
                  >
                    {item.name || ""}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Suggested Categories */}
        {querySuggestions?.[`categories.${lang}.lvl0`]?.length > 0 && (
          <div className="mt-4">
            <h4 className="text-lg font-semibold">
              {translation?.common_header_search_categories_title || ""}
            </h4>
            <ul>
              {querySuggestions?.[`categories.${lang}.lvl0`]?.map((item, index) => (
                <li key={index} className="mt-2 text-sm">
                  <button
                    className="text-start"
                    onClick={() => handleClickSuggestion(`/search?cat=${item.value}`)}
                  >
                    {item.value || ""}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Suggested Brands */}
        {querySuggestions?.[`brand.name.${lang}`]?.length > 0 && (
          <div className="mt-4">
            <h4 className="text-lg font-semibold">
              {translation?.common_header_search_brands_title || ""}
            </h4>
            <ul>
              {querySuggestions?.[`brand.name.${lang}`]?.map((item, index) => (
                <li key={index} className="mt-2 text-sm">
                  <button
                    className="text-start"
                    onClick={() => handleClickSuggestion(`/search?b=${item.value}`)}
                  >
                    {item.value || ""}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Suggestions */}
        {searchTerm && querySuggestions?.suggestion?.length > 0 && (
          <div className="mt-4">
            <h4 className="text-lg font-semibold">
              {translation?.common_header_search_suggestion_title || ""}
            </h4>
            <ul>
              {querySuggestions?.suggestion?.map((item, index) => (
                <li key={index} className="mt-2 text-sm">
                  <button
                    className="text-start"
                    onClick={() => handleClickSuggestion(`/search?q=${item.value}`)}
                  >
                    {item.value || ""}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {searchTerm && searchProducts.length > 0 && (
        <div className="mt-4 flex flex-col">
          <h2 className="mx-auto text-[1.25rem] font-semibold">
            {translation?.common_header_search_products_title || ""}
          </h2>
          <ProductCarousel
            products={searchProducts}
            translations={{ common: translation, plp: translation } as any}
            tryOn={enableVto}
            source={analyticEventSources.SEARCH}
          />
          <Button
            className="mx-auto my-8 text-start"
            onClick={() => handleClickSuggestion(`/search?q=${searchTerm}`, "carousel")}
          >
            {translation?.sale_page_view_all_button || ""}
          </Button>
        </div>
      )}
    </>
  );
};

export default SuggestionList;
